import {Card, CardContent} from '@mui/material';
import HighchartsReact from 'highcharts-react-official';
import * as Highcharts from 'highcharts';
import {useEffect, useRef, useState} from 'react';
import {COLORS, EXPORTING} from '../../../utils/constants';
import {
    AUTONOMOUS_KEY,
    BARELY_AUTONOMOUS_KEY,
    latestPerUoa,
    meanOf,
    percentageOf
} from '../../../utils/wia';
import {roundTo} from '../../../utils/round';

interface IAutonomyData {
    submissions: any[];
    uoa: string | number
}

interface ISubmissionData {
    autonomous: number;
    barelyAutonomous: number;
}

const EMPTY: ISubmissionData = {
    autonomous: 0,
    barelyAutonomous: 0,
};

const totalOf = (counts: ISubmissionData): number =>
    counts.autonomous + counts.barelyAutonomous;

const toPercentages = (submission: any): ISubmissionData => {
    const counts: ISubmissionData = {
        autonomous: submission[AUTONOMOUS_KEY] || 0,
        barelyAutonomous: submission[BARELY_AUTONOMOUS_KEY] || 0,
    };

    return {
        autonomous: percentageOf(counts.autonomous, totalOf(counts)),
        barelyAutonomous: percentageOf(counts.barelyAutonomous, totalOf(counts)),
    };
};

const Autonomy = ({submissions, uoa}: IAutonomyData) => {
    const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
    const [options, setOptions] = useState<Highcharts.Options>();

    const calculateSubmissions = () => {
        let currentSubmissions: ISubmissionData = EMPTY;
        let allSubmissions: ISubmissionData = EMPTY;

        const latest = latestPerUoa(submissions);
        const currentSubmission = latest.find((submission: any) => submission.uoa_id === uoa);

        if (currentSubmission) {
            currentSubmissions = toPercentages(currentSubmission);

            // The finalità is defined by question 3 of the scheda iniziale, and each activity weighs 1.
            const activities = latest
                .filter((submission: any) => submission.question4 === currentSubmission.question4)
                .map(toPercentages)
                .filter((percentages: ISubmissionData) => totalOf(percentages) > 0);

            allSubmissions = {
                autonomous: meanOf(activities.map((activity: ISubmissionData) => activity.autonomous)),
                barelyAutonomous: meanOf(activities.map((activity: ISubmissionData) => activity.barelyAutonomous)),
            };
        }

        const dashboardOptions: any = {
            chart: {
                height: 400,
                type: 'column',
                style: {
                    fontFamily: 'Rubik, sans-serif'
                },
                backgroundColor: '#fafafa'
            },
            title: {
                text: 'COMPOSIZIONE BENEFICIARI ATTESI PER LE ATTIVITA’ CHE HANNO LO STESSO OBIETTIVO - livello di autonomia',
                align: 'left'
            },
            xAxis: {
                categories: ['La mia attività', 'Media attività con stessa finalità in tutti i progetti']
            },
            yAxis: {
                min: 0,
                max: 100,
                labels: {
                    format: '{value}%'
                },
                title: {
                    text: ''
                },
                stackLabels: {
                    enabled: false,
                },
            },
            tooltip: {
                valueSuffix: '%'
            },
            plotOptions: {
                column: {
                    cursor: 'pointer',
                    stacking: 'percent',
                }
            },
            series: [
                {
                    name: '% di beneficiari anziani poco autonomi',
                    color: COLORS.red,
                    data: [
                        roundTo(currentSubmissions.barelyAutonomous),
                        roundTo(allSubmissions.barelyAutonomous),
                    ]
                },
                {
                    name: '% di beneficiari anziani autonomi',
                    color: COLORS.blue,
                    data: [
                        roundTo(currentSubmissions.autonomous),
                        roundTo(allSubmissions.autonomous),
                    ]
                }
            ],
            exporting: EXPORTING
        };

        setOptions(dashboardOptions);
    };

    useEffect(() => {
        calculateSubmissions();
    }, []);

    return (
        <Card>
            <CardContent>
                {
                    options && (
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={options}
                            ref={chartComponentRef}
                        />
                    )
                }
            </CardContent>
        </Card>
    );
};

export default Autonomy;
